import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Badge } from '@/shared/components/ui/badge';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Pagination } from '@/shared/components/custom/pagination/Pagination';
import { Toolbar } from '@/shared/components/custom/toolbar';
import { useServerPagination } from '@/shared/hooks/useServerPagination';
import { useMyOverdueTasks } from '../hooks/useMyOverdueTasks';
import { MyOverdueTasksTableRow } from './MyOverdueTasksTableRow';
import { MyOverdueTaskCard } from './MyOverdueTaskCard';
import type { MyOverdueTasksSortBy } from '../types';

const SORT_OPTIONS: { value: MyOverdueTasksSortBy; labelKey: string }[] = [
  { value: 'delayDays', labelKey: 'overdue.sort.delayDays' },
  { value: 'priority', labelKey: 'overdue.sort.priority' },
];

export function MyOverdueTasksTable() {
  const { t } = useTranslation('personalSummary');
  const [sortBy, setSortBy] = useState<MyOverdueTasksSortBy>('delayDays');

  const { data, isLoading, isError, refetch } = useMyOverdueTasks({
    sortBy,
    page: 1,
    pageSize: 10,
  });

  const pagination = useServerPagination({
    totalItems: data?.totalCount ?? 0,
    initialPageSize: 10,
  });

  const { data: pagedData } = useMyOverdueTasks({
    sortBy,
    page: pagination.apiParams.page,
    pageSize: pagination.apiParams.pageSize,
  });

  const items = pagedData?.items ?? data?.items ?? [];

  if (isLoading) {
    return (
      <Card>
        <CardContent
          className="space-y-2 p-4"
          aria-hidden="true"
          data-testid="personal-summary-overdue-loading-skeleton"
        >
          <Skeleton className="h-8 w-32" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent
          data-testid="personal-summary-overdue-error-message"
          className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950"
        >
          <p className="text-sm text-red-700 dark:text-red-400">{t('overdue.error')}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
            {t('common.retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="pb-0">
        <CardContent className="space-y-3 p-0">
          <Toolbar className="flex-wrap border-b px-4 pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
              <h3 className="text-base font-semibold md:text-lg">{t('sections.overdueTasks')}</h3>
              {(data?.totalCount ?? 0) > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {data?.totalCount}
                </Badge>
              )}
            </div>
            <Toolbar.Actions>
              <div
                className="flex items-center gap-2"
                data-testid="personal-summary-overdue-sort-controls"
              >
                <span className="text-xs text-muted-foreground">{t('overdue.sort.label')}</span>
                {SORT_OPTIONS.map(({ value, labelKey }) => (
                  <Button
                    key={value}
                    type="button"
                    size="sm"
                    variant={sortBy === value ? 'destructive' : 'outline'}
                    className="h-7 px-3 text-xs"
                    onClick={() => setSortBy(value)}
                  >
                    {t(labelKey)}
                  </Button>
                ))}
              </div>
            </Toolbar.Actions>
          </Toolbar>

          {!items.length ? (
            <div data-testid="personal-summary-overdue-empty-state" className="py-8 text-center">
              <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
              <p className="text-sm font-semibold">{t('overdue.empty.title')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('overdue.empty.description')}</p>
            </div>
          ) : (
            <>
              <div className="space-y-2 md:hidden" data-testid="personal-summary-overdue-cards">
                {items.map((task) => (
                  <MyOverdueTaskCard key={task.id} task={task} />
                ))}
              </div>

              <div
                className="hidden overflow-x-auto md:block"
                data-testid="personal-summary-overdue-table"
              >
                <table className="min-w-full table-auto">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th className="py-2 pl-4 pr-2 text-left font-medium">
                        {t('overdue.columns.task')}
                      </th>
                      <th className="px-2 py-2 text-left font-medium">
                        {t('overdue.columns.project')}
                      </th>
                      <th className="px-2 py-2 text-left font-medium">
                        {t('overdue.columns.dueDate')}
                      </th>
                      <th className="px-2 py-2 text-left font-medium">
                        {t('overdue.columns.delayDays')}
                      </th>
                      <th className="px-2 py-2 text-left font-medium">
                        {t('overdue.columns.priority')}
                      </th>
                      <th className="px-2 py-2 text-left font-medium">
                        {t('overdue.columns.status')}
                      </th>
                      <th className="px-2 py-2 pr-4 text-left font-medium">
                        {t('overdue.columns.blockedReason')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((task) => (
                      <MyOverdueTasksTableRow key={task.id} task={task} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {(pagedData?.totalCount ?? 0) > 10 && (
        <div data-testid="personal-summary-overdue-pagination">
          <Pagination {...pagination} />
        </div>
      )}
    </>
  );
}
